import { Typography, Box, Theme, styled } from '@quillbot/design-system';
import { makeStyles } from '@mui/styles';
import React, { useContext, useEffect, useState } from 'react';
import { PaymentScreens, PriceContext } from '../../models/paymentFlow';
import dynamic from 'next/dynamic';
import { ArrowBackIos } from '@mui/icons-material';
import { RootState } from 'src/_common/models/redux/store';
import { useSelector } from 'react-redux';
import SwipeableViews from 'react-swipeable-views';
import Plans from './Plans';
import { DEVICE_VIEWPORTS } from 'config/constants';
import { openToastr } from 'src/_common/handlers/services';
import { useRouter } from 'next/router';
import QuillLocalStorage from 'core/utils/localStorageClient';
import EmailVerifiedDialog from '../EmailVerifiedDialog';

const Checkout = dynamic(() => import('./Checkout'));
const Signup = dynamic(() => import('./Signup'));
const Login = dynamic(() => import('./Login'));

const StyledHeaderWrapper = styled('div')`
  align-items: center;
  max-height: 4.5em;
  margin-left: 1em;
  button {
    float: left;
  }
`;
const useStyles = makeStyles((theme) => ({
  checkoutTitle: {
    fontSize: '30px',
    fontWeight: 600,
    margin: '0px 0px 10px 0px ',
    textAlign: 'center',
    [theme.breakpoints.down('md')]: {
      fontWeight: 400,
      margin: '0px 0px 19px 0px ',
      textAlign: 'center',
    },
  },

  checkoutTitlePlans: {
    fontSize: '30px',
    fontWeight: 600,
    margin: '0px 0px 20px 0px ',
    textAlign: 'center',
    [theme.breakpoints.down('md')]: {
      fontWeight: 400,
      margin: '0px 0px 15px 0px ',
      textAlign: 'left',
    },
  },

  leftAlignWithCenter: {
    [theme.breakpoints.up('md')]: {
      textAlign: 'left!important',
    },
    [theme.breakpoints.down('lg')]: {
      textAlign: 'center!important',
    },
  },

  checkoutTitleMobile: {
    [theme.breakpoints.down('md')]: {
      margin: '25px auto 27px',
      textAlign: 'center',
      fontWeight: 400,
      maxWidth: '330px',
      padding: '0px 25px',
      fontSize: '25px',
    },
  },

  planContainer: {
    background: '#f5f5f5',
    marginTop: '30px',
    padding: '35px 40px 40px',
    [theme.breakpoints.down('md')]: {
      padding: '20px 0px 40px',
      flexDirection: 'column-reverse',
    },
  },
  checkoutMainHolder: {
    [theme.breakpoints.down('sm')]: {
      paddingLeft: '11px',
      paddingRight: '11px',
      paddingBottom: '20px',
    },
    [theme.breakpoints.down(460)]: {
      marginBottom: '-41px',
    },
  },
  upgradeContainer: {
    [theme.breakpoints.down('md')]: {
      maxWidth: '500px',
      margin: '0px auto',
    },
  },
  chackoutSmallText: {
    fontSize: '10px',
    maxWidth: '496px',
    margin: '15px 0px 0px',
    textAlign: 'left',
  },
  paymentMainHolder: {
    [theme.breakpoints.down('md')]: {
      marginBottom: '0px',
    },
  },
  signupBoxHolder: {
    maxWidth: '500px',
    margin: '29px auto 20px',
    [theme.breakpoints.down('lg')]: {
      maxWidth: '90%',
    },
    [theme.breakpoints.down('md')]: {
      maxWidth: '95%',
      background: '#fff',
      margin: '20px auto 10px',
      borderRadius: '5px',
    },
  },

  signupBoxHolderNew: {
    maxWidth: '500px',
    margin: '29px auto 20px',

    [theme.breakpoints.down('lg')]: {
      maxWidth: '90%',
    },

    [theme.breakpoints.down('md')]: {
      maxWidth: '95%',
      background: '#fff',
      margin: '30px auto 5px',
      borderRadius: '5px',
    },
  },

  backBtnHolder: {
    position: 'relative',
    width: '100%',
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
    '&:h2': {
      width: 'calc(100% - 90px)',
    },
  },

  backBtn: {
    marginTop: '6px',
    cursor: 'pointer',

    '& :hover': {
      color: theme.palette.primary.main,
    },
  },

  languageBoxMain: {
    maxWidth: '455px',
    [theme.breakpoints.down('md')]: {
      margin: 'auto',
    },
  },

  backBtnMobile: {
    color: theme.palette.primary.main,
    marginTop: '14px',
    cursor: 'pointer',
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },

  checkoutPlanText: { color: theme.palette.text.primary },
}));

const StyledPlanName = styled('b')`
  font-weight: 600;
  color: ${({ theme }: { theme: Theme }) => theme.palette.secondary.main};
  // span {
  //   color: #252525;
  // }
  @media ${DEVICE_VIEWPORTS.md} {
    font-weight: 400;
    color: ${({ theme }: { theme: Theme }) => theme.palette.secondary.main};
    span {
      display: none;
    }
  }
  @media ${DEVICE_VIEWPORTS.sm} {
    font-weight: 400;
    color: ${({ theme }: { theme: Theme }) => theme.palette.secondary.main};
    span {
      display: none;
    }
  }
  @media ${DEVICE_VIEWPORTS.xs} {
    font-weight: 400;
    color: ${({ theme }: { theme: Theme }) => theme.palette.secondary.main};
    span {
      display: none;
    }
  }
`;

export default function PaymentSwipeView({ forcePlan }: { forcePlan?: number | null }) {
  const isAuthenticated = useSelector((root: RootState) => root.user?.isAuthenticated);
  const [screen, setScreen] = useState<{ screenIndex: PaymentScreens; selectedPlan: number }>({
    screenIndex: (() => {
      if (forcePlan) {
        return isAuthenticated ? PaymentScreens.CHECKOUT : PaymentScreens.LOGIN;
      }

      return PaymentScreens.PLANS;
    })(),
    selectedPlan: forcePlan || -1, // no plans selected
  });
  const isPremiumUser = useSelector((state: RootState) => state.user?.premium);
  const isSyncedWithDb = useSelector((state: RootState) => state.user?.syncedWithDb);
  const isSubPaused = useSelector((state: RootState) => state.user?.isSubPaused);
  const isEmailVerified = useSelector((state: RootState) => state.user.emailVerified);

  // email verification
  const [showEmailVerificationDialog, setShowEmailVerificationDialog] = useState(false);

  const closeEmailVerificationDialog = () => {
    setShowEmailVerificationDialog(false);
  };

  const handleEmailVerificationSuccess = () => {
    openToastr('Email sent.');
    setShowEmailVerificationDialog(false);
  };

  const classes = useStyles();
  const router = useRouter();

  useEffect(() => {
    if (prices.length < 3) openToastr('Something went wrong, please refresh the page!');

    if (router.query.token) {
      setScreen({
        screenIndex: PaymentScreens.CHECKOUT,
        selectedPlan: Number(QuillLocalStorage.getInstance().get('selectedPlan')),
      });
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setScreen({
        screenIndex: PaymentScreens.PLANS,
        selectedPlan: -1,
      });
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isPremiumUser) setScreen({ ...screen, screenIndex: PaymentScreens.PLANS });
    else if (
      isSyncedWithDb &&
      isLoggedInUser &&
      (screen.screenIndex === PaymentScreens.LOGIN || screen.screenIndex === PaymentScreens.SIGNUP)
    ) {
      if (isEmailVerified) {
        setScreen({ ...screen, screenIndex: PaymentScreens.CHECKOUT });
      } else {
        setScreen({ ...screen, screenIndex: PaymentScreens.PLANS });
      }
    }
    // handle direct urls
    else if (isSyncedWithDb && router.query.p && !isSubPaused) {
      const selectedPlan = prices.find((item) => item.duration === Number(router.query.p))?.id;
      if (isLoggedInUser && !isEmailVerified) {
        setShowEmailVerificationDialog(true);
      } else if (selectedPlan) {
        setScreen((prevState) => ({
          ...prevState,
          screenIndex: isLoggedInUser ? PaymentScreens.CHECKOUT : PaymentScreens.LOGIN,
          selectedPlan,
        }));
      }
    }
  }, [isPremiumUser, isSyncedWithDb]);

  const isLoggedInUser = useSelector((state: RootState) => state.user?.isAuthenticated);
  const prices = useContext(PriceContext);

  const screenHeaderArr = [
    <Typography variant="h2" className={classes.checkoutTitle} key="yourplanheader">
      Select your plan
    </Typography>,
    <StyledHeaderWrapper key="showpriceheader">
      <Box display="flex" alignItems="flex-start" className={classes.backBtnHolder}>
        <ArrowBackIos
          className={classes.backBtn}
          onClick={() =>
            setScreen({
              screenIndex: PaymentScreens.PLANS,
              selectedPlan: -1, // no plans selected
            })
          }
        />
        <Typography
          className={classes.checkoutTitle}
          style={{ width: 'calc(100% - 90px)' }}
          variant="h2"
        >
          Sign Up
        </Typography>
      </Box>
    </StyledHeaderWrapper>,

    <StyledHeaderWrapper key="showpriceheader">
      <Box display="flex" alignItems="flex-start" className={classes.backBtnHolder}>
        <ArrowBackIos
          className={classes.backBtn}
          onClick={() =>
            setScreen({
              screenIndex: PaymentScreens.PLANS,
              selectedPlan: -1, // no plans selected
            })
          }
        />

        <Typography
          className={classes.checkoutTitle}
          style={{ width: 'calc(100% - 90px)' }}
          variant="h2"
        >
          Login
        </Typography>
      </Box>
    </StyledHeaderWrapper>,

    <StyledHeaderWrapper key="showpriceheader">
      <Box display="flex" alignItems="flex-start" className={classes.backBtnHolder}>
        <ArrowBackIos
          className={classes.backBtn}
          onClick={() =>
            setScreen({
              screenIndex: PaymentScreens.PLANS,
              selectedPlan: -1, // no plans selected
            })
          }
        />
        <Typography
          className={classes.checkoutTitlePlans}
          style={{ width: 'calc(100% - 90px)' }}
          variant="h2"
        >
          <StyledPlanName>
            <span className={classes.checkoutPlanText}>Selected: </span>
            {prices[screen.selectedPlan]?.title} {screen.selectedPlan !== 2 ? 'Plan' : ''}
          </StyledPlanName>
        </Typography>
        <div
          role="button"
          className={classes.backBtnMobile}
          onClick={() =>
            setScreen({
              screenIndex: PaymentScreens.PLANS,
              selectedPlan: -1, // no plans selected
            })
          }
        >
          Change
        </div>
      </Box>
    </StyledHeaderWrapper>,
  ];

  const getSwipeViews = (authenticated: boolean) => {
    const views = [
      <Plans
        setScreen={setScreen}
        key="plans"
        setShowEmailVerificationDialog={setShowEmailVerificationDialog}
      />,
    ];

    if (!authenticated) {
      views.push(
        <Box boxShadow={3} className={classes.signupBoxHolderNew} key="signup">
          <Signup setScreen={setScreen} planID={screen.selectedPlan} />
        </Box>
      );

      views.push(
        <Box boxShadow={3} className={classes.signupBoxHolder} key="login">
          <Login setScreen={setScreen} planID={screen.selectedPlan} />
        </Box>
      );
    } else views.push(<Checkout plan={prices[screen.selectedPlan]} key="checkout" />);

    return views;
  };

  return (
    <>
      {screenHeaderArr[screen.screenIndex] ? (
        screenHeaderArr[
          screen.screenIndex === PaymentScreens.CHECKOUT && isLoggedInUser && isSyncedWithDb
            ? 3
            : screen.screenIndex
        ]
      ) : (
        <></>
      )}

      <SwipeableViews disabled index={screen.screenIndex}>
        {getSwipeViews(!!(typeof isSyncedWithDb === 'boolean' ? isLoggedInUser : isAuthenticated))}
      </SwipeableViews>
      <EmailVerifiedDialog
        isVisible={showEmailVerificationDialog}
        handleClose={closeEmailVerificationDialog}
        handleSuccess={handleEmailVerificationSuccess}
      />
    </>
  );
}
